import { useMemo } from 'react';

import { useNaptime } from 'bundles/naptimejs';
import ExternallyAccessibleNostosV1 from 'bundles/naptimejs/resources/externallyAccessibleNostos.v1';
import type { SearchExplainQueryMetrics } from 'bundles/search-common/components/internal-search-explainer/types';

type NostosMetricsResponse = Array<{ content?: string | SearchExplainQueryMetrics }>;

const isSearchExplainQueryMetrics = (obj: unknown): obj is SearchExplainQueryMetrics => {
  return typeof obj === 'object' && obj !== null && 'queryOccurrences' in obj;
};

/**
 * Hook to fetch search explain metrics from Nostos using Naptime
 * When disabled, returns early without making API calls
 */
export const useSearchExplainMetrics = (query: string, enabled: boolean) => {
  const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);
  const shouldFetch = enabled && !!normalizedQuery;

  const { data, pending } = useNaptime<{
    metricsData?: NostosMetricsResponse;
  }>(() => {
    if (!shouldFetch) {
      return {};
    }

    return {
      metricsData: ExternallyAccessibleNostosV1.finder('getAllProperties', {
        params: {
          // eslint-disable-next-line camelcase
          job_name: 'search_explainer_query_metrics',
          keys: normalizedQuery,
        },
      }),
    };
  }, [shouldFetch, normalizedQuery]);

  const metrics = useMemo(() => {
    if (!shouldFetch || !data?.metricsData?.[0]?.content) {
      return null;
    }

    const content = data.metricsData[0].content;

    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      return isSearchExplainQueryMetrics(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [data, shouldFetch]);

  return {
    metrics,
    loading: shouldFetch ? pending : false,
    error: undefined,
  };
};

export default useSearchExplainMetrics;
